import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { env } from '@/config/env';
import { reactQueryKeys } from '@/config/react-query';
import { useCodWeb } from '@/hooks/use-codweb';
import { fetchQueueRequest } from '@/lib/fetch-queue-request';
import { getApiHeaders } from '@/lib/getApiHeaders';
import {
  HttpError,
  queueRetryDelay,
  shouldRetryQueueRequest,
} from '@/lib/http-error';

export const BranchFeaturesSchema = z.object({ queueMetrics: z.boolean() });

export const getBranchFeatures = async (
  codWeb: string,
  signal?: AbortSignal
) => {
  const headers = await getApiHeaders();
  return fetchQueueRequest(
    `${env.VITE_BACKEND_URL}/api/${codWeb}/features`,
    { headers, signal },
    async (response) => {
      if (!response.ok) throw await HttpError.fromResponse(response);
      return BranchFeaturesSchema.parse(await response.json());
    }
  );
};

export const useBranchFeatures = () => {
  const codWeb = useCodWeb();
  return useQuery({
    queryKey: [reactQueryKeys.branchFeatures, codWeb],
    queryFn: ({ signal }) => getBranchFeatures(codWeb, signal),
    enabled: Boolean(codWeb),
    // Branch capabilities are stable for this page session; reload after configuration changes.
    // A background capability lookup must not interrupt an active phone session.
    staleTime: Infinity,
    retry: shouldRetryQueueRequest,
    retryOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retryDelay: queueRetryDelay,
  });
};

export const useHasQueueMetrics = (): boolean | undefined => {
  const query = useBranchFeatures();
  return query.isError ? undefined : query.data?.queueMetrics;
};
